// Assistant panel contracts (spec `docs/superpowers/specs/2026-09-28-assistant-panel-design.md`
// §3.1, §9): the conversational turn/reply shapes stored on `conversation_messages.metadata`
// (≤16 KB), the fixed action catalog the assistant may propose (§4), and the `assistant` section
// of the routing configuration (per-question / per-day cost ceilings, council condition 4).
import { z } from "zod";
import type { Role } from "./enums.ts";
import { MESSAGE_ROLES } from "./model.ts";
import { githubOperationRequestSchema } from "./github-operator.ts";

const id = z.uuid();
const timestamp = z.iso.datetime();

// ---------- Vocabulary ----------

/**
 * `conversation_sessions.backend` tag of a panel turn session (fix round 1, R16): the panel API
 * (`POST /v1/assistant/turns` and siblings) creates and reads only sessions carrying this tag, and
 * the legacy chat routes (`/v1/projects/:id/assistant/messages`, `/v1/assistant/messages`) refuse to
 * list, read or continue one — without this discriminator the two surfaces were indistinguishable
 * (same role "assistant", same task_id null), so a panel session's author-only turns were readable by
 * any project member (or an API token) through the legacy reader, and the legacy writer could append
 * to another author's panel session.
 */
export const ASSISTANT_PANEL_BACKEND = "assistant_panel";

/** Private conversation history is separate from the project catalog. */
export const assistantSessionSummarySchema = z.object({
  id,
  project_id: id.nullable(),
  first_message: z.string().max(200).nullable(),
  created_at: timestamp,
  last_activity_at: timestamp,
});
export type AssistantSessionSummary = z.infer<typeof assistantSessionSummarySchema>;
export const assistantSessionsQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(50).default(20),
  cursor: id.optional(),
});
export const assistantSessionsResponseSchema = z.object({
  items: z.array(assistantSessionSummarySchema),
  next_cursor: id.nullable(),
});
export type AssistantSessionsResponse = z.infer<typeof assistantSessionsResponseSchema>;

export const ASSISTANT_FOCUS = ["readiness", "task", "config", "kanban", "runs", "summary", "general"] as const;
export type AssistantFocus = (typeof ASSISTANT_FOCUS)[number];

export const ASSISTANT_REPLY_MODES = ["ai", "rules", "not_configured", "budget_exhausted", "error"] as const;
export type AssistantReplyMode = (typeof ASSISTANT_REPLY_MODES)[number];

/**
 * D36 working mode of a turn (the composer's mode selector), enforced server-side by the domain (`normalizeProposal`),
 * never by the model: `chat` = read tools only (no action but `open_page`), `plan` = planning actions only (nothing
 * runs code), `assisted` = every action waits for a click, `autonomous` = the D34 tiers.
 */
export const ASSISTANT_MODES = ["chat", "plan", "assisted", "autonomous"] as const;
export type AssistantMode = (typeof ASSISTANT_MODES)[number];
export const DEFAULT_ASSISTANT_MODE: AssistantMode = "autonomous";

/** PRD Skills v1.1 §21.3: proportional execution path for requests. */
export const WORK_PATHS = ["fast", "standard", "planned"] as const;
export type WorkPath = (typeof WORK_PATHS)[number];

export const workPathDecisionSchema = z
  .object({
    work_path: z.enum(WORK_PATHS),
    reason: z.string().max(500),
    risk: z.enum(["low", "medium", "high"]),
    target_evidence: z.string().max(1000).optional(),
    required_decisions: z.array(z.string().max(200)).max(10).optional(),
    planning_artifacts_required: z.boolean().default(false),
  })
  .strict();
export type WorkPathDecision = z.infer<typeof workPathDecisionSchema>;

/** PRD Skills v1.1 §22.3: code location facade types. */
export const LOCATE_CODE_TARGET_TYPES = ["ui_text", "route", "symbol", "file"] as const;
export type LocateCodeTargetType = (typeof LOCATE_CODE_TARGET_TYPES)[number];

export const locateCodeInputSchema = z
  .object({
    query: z.string().min(1).max(300),
    target_type: z.enum(LOCATE_CODE_TARGET_TYPES).optional(),
    path_hint: z.string().max(500).optional(),
    route_hint: z.string().max(500).optional(),
  })
  .strict();
export type LocateCodeInput = z.infer<typeof locateCodeInputSchema>;

export interface LocateCodeCandidate {
  path: string;
  line: number;
  excerpt: string;
  evidence_type: string;
}

export interface LocateCodeResult {
  query: string;
  target_type?: LocateCodeTargetType;
  candidates: LocateCodeCandidate[];
  relationship?: string;
  observed_at: string;
  content_hash?: string;
  coverage: "lexical" | "structured";
  truncated: boolean;
  ambiguity: "low" | "medium" | "high";
  notes?: string;
}

export const ASSISTANT_TURN_STATES = ["pending", "running", "done", "cancelled", "failed"] as const;
export type AssistantTurnState = (typeof ASSISTANT_TURN_STATES)[number];

export const ASSISTANT_ACTION_TYPES = [
  "validate_project",
  "open_page",
  "retry_task",
  "unblock_task",
  "resource_available",
  "start_project",
  "resume_project",
  "create_task",
  "add_guidance",
  "open_settings_form",
  "decide_approval",
  "connect_repository",
  "create_project_from_brief",
  // D34 (assistant as operator): the rest of the API surface the assistant may act on.
  "update_project_settings",
  "set_execution_policy",
  "edit_task",
  "move_task",
  "cancel_task",
  "start_task_run",
  "pause_project",
  "drain_project",
  "run_model_probe",
  "update_agent_profile",
  "update_model_target",
  "update_provider_connection",
  "activate_integration",
  "activate_routing_config",
  "publish_project",
  // D36 (working modes and the system operator on Home).
  "start_planning_run",
  "create_project_request",
  "create_system_task",
  "run_guardian",
  "guardian_incident_action",
  "review_learnings",
  "server_operation",
  "github_operation",
  // D37.3: translate the not-yet-started tasks of a project into its content language (the planner's model, capped).
  "translate_tasks",
  // RF-DEL-01: delete a project (always a click; the card shows the name and task counts).
  "delete_project",
] as const;
export type AssistantActionType = (typeof ASSISTANT_ACTION_TYPES)[number];

/**
 * D34 sensitivity tier: `auto` runs by itself right after the reply (shown as a done step), `confirm` waits for a click.
 * The engine stamps it from this catalog (`normalizeProposal`), never from the model.
 */
export const ASSISTANT_ACTION_TIERS = ["auto", "confirm"] as const;
export type AssistantActionTier = (typeof ASSISTANT_ACTION_TIERS)[number];

export interface AssistantActionMeta {
  tier: AssistantActionTier;
  /**
   * The tier when the turn read untrusted content (learnings, PRD/task/log free text, §9 C7): an auto action that is
   * irreversible, changes where code comes from, or spends money is downgraded to a click then.
   */
  taintedTier: AssistantActionTier;
  /** Minimum role (project role, or organization role for `scope: "organization"`); the API keeps its own checks. */
  minRole: Role;
  /** What the action touches: a project, a task (its project), organization-wide configuration, or nothing. */
  scope: "project" | "task" | "organization" | "none";
  /** The API call the web makes with the user's session (documentation; the web executor is the implementation). */
  api: string;
  /** i18n key of the card/step title in the web (`apps/web/src/i18n/en-assistant.ts`). */
  title: `assistant.actionTitle.${string}`;
  /**
   * D36: keeps its `auto` tier in a Home session (no project) in autonomous mode — every other type is a click there.
   * Only organization-level actions that never navigate and never touch secrets, money or configuration.
   */
  homeAuto?: true;
}

const auto = (minRole: Role, scope: AssistantActionMeta["scope"], api: string, title: AssistantActionMeta["title"], taintedTier: AssistantActionTier = "auto"): AssistantActionMeta => ({
  tier: "auto",
  taintedTier,
  minRole,
  scope,
  api,
  title,
});
const confirm = (minRole: Role, scope: AssistantActionMeta["scope"], api: string, title: AssistantActionMeta["title"]): AssistantActionMeta => ({
  tier: "confirm",
  taintedTier: "confirm",
  minRole,
  scope,
  api,
  title,
});

/**
 * The action catalog with its sensitivity tier (D34, `docs/delivery/assistant-operator-plan.md` §1): "executes, and only
 * confirms what is sensitive". Confirm: secrets/keys, paid models or budgets, deleting, publishing, the command
 * execution policy, organization-wide configuration and approvals. Navigation into a form or a connection flow stays a
 * click too (it moves the user).
 */
export const ASSISTANT_ACTION_CATALOG: Readonly<Record<AssistantActionType, AssistantActionMeta>> = {
  validate_project: auto("maintainer", "project", "POST /v1/projects/{id}/validate", "assistant.actionTitle.validate_project"),
  open_page: auto("observer", "none", "(navigation)", "assistant.actionTitle.open_page"),
  retry_task: auto("operator", "task", "POST /v1/tasks/{id}/actions retry", "assistant.actionTitle.retry_task"),
  unblock_task: auto("operator", "task", "POST /v1/tasks/{id}/actions unblock", "assistant.actionTitle.unblock_task"),
  resource_available: auto("operator", "task", "POST /v1/tasks/{id}/actions resource_available", "assistant.actionTitle.resource_available"),
  start_project: auto("maintainer", "project", "POST /v1/projects/{id}/execution start", "assistant.actionTitle.start_project"),
  resume_project: auto("maintainer", "project", "POST /v1/projects/{id}/execution resume", "assistant.actionTitle.resume_project"),
  create_task: auto("operator", "project", "POST /v1/projects/{id}/assistant/messages create_task", "assistant.actionTitle.create_task"),
  add_guidance: auto("operator", "task", "POST /v1/projects/{id}/assistant/messages add_guidance", "assistant.actionTitle.add_guidance"),
  open_settings_form: confirm("maintainer", "project", "(navigation)", "assistant.actionTitle.open_settings_form"),
  decide_approval: confirm("operator", "none", "POST /v1/approvals/{id}/decide", "assistant.actionTitle.decide_approval"),
  // Maps the repository and clones it: the connect route requires an organization admin (existing-project onboarding).
  connect_repository: confirm("admin", "project", "(navigation)", "assistant.actionTitle.connect_repository"),
  // D28: a project, its private GitHub repository and a planning run; no project scope (Home and any project session).
  create_project_from_brief: confirm("admin", "none", "POST /v1/projects/from-brief", "assistant.actionTitle.create_project_from_brief"),
  // A repository URL change moves where the agents' code comes from: a click when the turn read untrusted content.
  update_project_settings: auto("maintainer", "project", "PATCH /v1/projects/{id}", "assistant.actionTitle.update_project_settings", "confirm"),
  set_execution_policy: confirm("admin", "project", "POST /v1/projects/{id}/execution-policy", "assistant.actionTitle.set_execution_policy"),
  edit_task: auto("operator", "task", "PATCH /v1/tasks/{id}", "assistant.actionTitle.edit_task"),
  move_task: auto("operator", "task", "POST /v1/tasks/{id}/actions", "assistant.actionTitle.move_task"),
  // Irreversible for the task's work in flight: a click when the turn read untrusted content.
  cancel_task: auto("operator", "task", "POST /v1/tasks/{id}/actions cancel", "assistant.actionTitle.cancel_task", "confirm"),
  start_task_run: auto("maintainer", "task", "POST /v1/tasks/{id}/runs", "assistant.actionTitle.start_task_run"),
  pause_project: auto("maintainer", "project", "POST /v1/projects/{id}/execution pause", "assistant.actionTitle.pause_project"),
  drain_project: auto("maintainer", "project", "POST /v1/projects/{id}/execution drain", "assistant.actionTitle.drain_project"),
  // A probe spends tokens (capped at ASSISTANT_PROBE_MAX_CENTS): a click when the turn read untrusted content.
  run_model_probe: auto("admin", "organization", "POST /v1/model-targets/{id}/capability-probes", "assistant.actionTitle.run_model_probe", "confirm"),
  update_agent_profile: confirm("admin", "organization", "PATCH /v1/agent-profiles/{id}", "assistant.actionTitle.update_agent_profile"),
  update_model_target: confirm("admin", "organization", "PATCH /v1/model-targets/{id}", "assistant.actionTitle.update_model_target"),
  update_provider_connection: confirm("admin", "organization", "PATCH /v1/provider-connections/{id}", "assistant.actionTitle.update_provider_connection"),
  activate_integration: confirm("admin", "organization", "POST /v1/integrations/{id}/activate", "assistant.actionTitle.activate_integration"),
  activate_routing_config: confirm("admin", "organization", "POST /v1/routing-configs/{id}/activate", "assistant.actionTitle.activate_routing_config"),
  publish_project: confirm("admin", "project", "POST /v1/projects/{id}/publish", "assistant.actionTitle.publish_project"),
  // ---------- D36 ----------
  // A planning run of the project's latest PRD (the API refuses a project without one); nothing runs code.
  start_planning_run: auto("maintainer", "project", "POST /v1/projects/{id}/planning-runs", "assistant.actionTitle.start_planning_run"),
  create_project_request: { ...auto("maintainer", "project", "POST /v1/projects/{id}/assistant/requests", "assistant.actionTitle.create_project_request"), homeAuto: true },
  // A MaestrOS problem handed to the agents: a task on the project whose repository is MaestrOS (the guardian's project
  // resolution; the domain stamps `project_id`, never the model). Runs by itself from Home in autonomous mode.
  create_system_task: { ...auto("operator", "organization", "POST /v1/projects/{id}/tasks (the MaestrOS project)", "assistant.actionTitle.create_system_task"), homeAuto: true },
  run_guardian: confirm("admin", "organization", "POST /v1/guardian/run", "assistant.actionTitle.run_guardian"),
  guardian_incident_action: confirm("admin", "organization", "POST /v1/guardian/incidents/{id}/actions", "assistant.actionTitle.guardian_incident_action"),
  review_learnings: { ...auto("admin", "organization", "POST /v1/learning-governor/run", "assistant.actionTitle.review_learnings"), homeAuto: true },
  server_operation: { ...auto("admin", "organization", "POST /v1/server-operations", "assistant.actionTitle.server_operation", "confirm"), homeAuto: true },
  github_operation: confirm("admin", "organization", "POST /v1/github-operations", "assistant.actionTitle.github_operation"),
  // ---------- D37.3 ----------
  // Rewrites the text of every unstarted task with a model (cost-capped; a paid planner model needs a second consent).
  translate_tasks: confirm("maintainer", "project", "POST /v1/projects/{id}/task-translations", "assistant.actionTitle.translate_tasks"),
  // ---------- RF-DEL-01 ----------
  // Irreversible: always a click (never auto, tainted or not). The API allows the organization admin or the project's
  // maintainer (owner) and refuses while work is running.
  delete_project: confirm("maintainer", "project", "DELETE /v1/projects/{id}", "assistant.actionTitle.delete_project"),
};

/** D34 loop bound: at most this many automatic follow-up turns per user question (the web also offers Stop). */
export const ASSISTANT_MAX_FOLLOW_UPS = 5;
/** Spending cap of an assistant-started model probe (a probe consumes tokens, RF-CFG-10). */
export const ASSISTANT_PROBE_MAX_CENTS = 5;
/** Task actions `move_task` may request (the transition rules of the API still apply). */
export const ASSISTANT_MOVE_TASK_ACTIONS = ["mark_ready", "pause", "resume", "reopen", "block"] as const;

/** App routes `open_page` may navigate to (tab ids copied from `ProjectPage.tsx` and `SettingsPage.tsx`). */
const UUID_SEGMENT = "[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}";
export const ASSISTANT_PAGE_PATTERNS: readonly RegExp[] = [
  /^\/$/,
  new RegExp(`^/chats/${UUID_SEGMENT}$`),
  /^\/projects$/,
  new RegExp(`^/projects/${UUID_SEGMENT}(/(overview|kanban|prds|code|runs|preview|signals|memory|settings))?$`),
  /^\/settings\/(general|infrastructure|providers|models|agents|quotas|routing|context|gauntlet|history|notifications|permissions)$/,
  /^\/(runs|prds|memory|inbox|agents|integrations|observability)$/,
];

// ---------- Context and actions ----------

/** Page context sent with a turn: ids and a route template only, never page text (§6). */
export const assistantContextSchema = z
  .object({
    route: z.string().max(200),
    project_id: id.nullable(),
    task_id: id.nullable(),
    focus: z.enum(ASSISTANT_FOCUS),
    /** D34: the UI locale (BCP 47, e.g. `pt-BR`): the assistant answers in it. Absent ⇒ the question's language. */
    locale: z
      .string()
      .regex(/^[a-z]{2,3}(-[A-Za-z0-9]{2,8})?$/)
      .optional(),
    /**
     * D34: an automatic follow-up turn after the auto actions of `reply_message_id` (a reply in the same session) ran;
     * the engine reads their outcomes from that reply's claim ledger, never from this request. `round` counts the
     * follow-ups of one user question (1..ASSISTANT_MAX_FOLLOW_UPS).
     */
    follow_up: z
      .object({ reply_message_id: id, round: z.number().int().min(1).max(ASSISTANT_MAX_FOLLOW_UPS) })
      .strict()
      .optional(),
    /** D36: the working mode (absent ⇒ `autonomous`, the D34 behaviour). */
    mode: z.enum(ASSISTANT_MODES).optional(),
    /** D36 composer: the agent the user picked on Home (a hint for the assistant; ids only). */
    agent_profile_id: id.optional(),
    /** D36 composer: integrations / module instances the user picked as context hints (ids only). */
    integration_ids: z.array(id).max(10).optional(),
  })
  .strict();
export type AssistantContext = z.infer<typeof assistantContextSchema>;

const actionModeSchema = z.enum(["auto", "confirm"]);
const actionLabelSchema = z.string().trim().min(1).max(80);
const actionReasonSchema = z.string().trim().min(1).max(200);
const expectedVersionSchema = z.number().int();
/**
 * Display-only subject of an action card (`#N` for a task, a project's name for a project page), always set by the
 * domain (`normalizeProposal`) from trusted facts — whatever a proposal sends is overwritten (R29). The card's title
 * and button come from i18n by type + args + subject; `label`/`reason` are the model's words (secondary text only,
 * hidden when the reply is tainted) or, from the rules path, the action type and a reason code. At most 48 chars: with
 * four actions and four claims (`via` included) a maximal reply stays under the 16 KB metadata check (measured 16 312 B
 * as jsonb, `conversation_messages_metadata_size`).
 */
export const ASSISTANT_SUBJECT_MAX = 48;
const actionSubjectSchema = z.string().max(ASSISTANT_SUBJECT_MAX).nullable().optional();

/** `open_page` may only navigate within the allowlist above (never an external URL or scheme). */
const openPagePathSchema = z
  .string()
  .trim()
  .min(1)
  .max(300)
  .refine((path) => ASSISTANT_PAGE_PATTERNS.some((pattern) => pattern.test(path)), { message: "path is not an allowlisted app route" });

/**
 * `create_project_from_brief` (D28): the project name follows `POST /v1/projects/from-brief` (1..80). The endpoint
 * accepts a brief of up to 20 000 characters, but a proposed action is stored inside the reply on
 * `conversation_messages.metadata`, whose check is 16 KB (`conversation_messages_metadata_size`, compressed size) next
 * to the answer text and up to three other actions — so an assistant-proposed brief is capped at 4000 characters, the
 * same as the answer. A reply with a full brief AND a maximal multi-byte answer AND three other maximal actions could
 * still exceed the check uncompressed; realistic replies (one brief, a short answer) stay well below it. The assistant
 * is told to keep the brief small (about 3-6 tasks); a longer one fails the schema and never reaches a card.
 */
export const ASSISTANT_BRIEF_NAME_MAX = 80;
export const ASSISTANT_BRIEF_MAX = 4000;

/**
 * D34: set by the domain (never by a proposal) when the caller's role cannot run the action — the web shows it as
 * refused and never attempts it.
 */
const actionRefusedSchema = z.literal("role").optional();

function actionVariant<Type extends string, Args extends z.ZodRawShape>(type: Type, args: z.ZodObject<Args>) {
  return z
    .object({ type: z.literal(type), mode: actionModeSchema, label: actionLabelSchema, reason: actionReasonSchema, subject: actionSubjectSchema, refused: actionRefusedSchema, args })
    .strict();
}

/** At least one field besides the ids (an empty patch is not an action). */
function someField(ids: readonly string[]) {
  return (value: Record<string, unknown>) => Object.entries(value).some(([k, v]) => !ids.includes(k) && v !== undefined);
}

const shortReason = z.string().trim().min(1).max(500);
const branchSchema = z.string().trim().min(1).max(200);
const commandSchema = z.string().trim().min(1).max(1000);

/** D34 `update_project_settings`: the project fields of `PATCH /v1/projects/{id}` plus the first workspace's commands. */
const projectSettingsArgs = z
  .object({
    project_id: id,
    name: z.string().trim().min(1).max(200).optional(),
    description: z.string().max(2000).optional(),
    repository_url: z.string().trim().min(1).max(500).optional(),
    base_branch: branchSchema.optional(),
    test_command: commandSchema.optional(),
    build_command: commandSchema.optional(),
    workspace_subpath: z
      .string()
      .trim()
      .max(300)
      .refine((v) => !v.split("/").includes(".."), { message: "subpath must stay inside the workspace" })
      .optional(),
  })
  .strict()
  .refine(someField(["project_id"]), { message: "at least one setting" });

const editTaskArgs = z
  .object({
    task_id: id,
    title: z.string().trim().min(1).max(200).optional(),
    description: z.string().max(4000).optional(),
    acceptance_criteria: z.array(z.string().trim().min(1).max(500)).max(20).optional(),
  })
  .strict()
  .refine(someField(["task_id"]), { message: "at least one field" });

const agentProfileArgs = z
  .object({ agent_profile_id: id, model_target_id: id.nullable().optional(), fallback_model_target_ids: z.array(id).max(10).optional(), state: z.enum(["active", "disabled"]).optional() })
  .strict()
  .refine(someField(["agent_profile_id"]), { message: "at least one field" });

/** The fixed action catalog (spec §4): nothing outside this union can be proposed or executed. */
export const assistantActionSchema = z.discriminatedUnion("type", [
  actionVariant("validate_project", z.object({ project_id: id }).strict()),
  actionVariant("open_page", z.object({ path: openPagePathSchema }).strict()),
  actionVariant("retry_task", z.object({ task_id: id, expected_version: expectedVersionSchema }).strict()),
  actionVariant("unblock_task", z.object({ task_id: id, expected_version: expectedVersionSchema }).strict()),
  actionVariant("resource_available", z.object({ task_id: id, expected_version: expectedVersionSchema }).strict()),
  actionVariant("start_project", z.object({ project_id: id }).strict()),
  actionVariant("resume_project", z.object({ project_id: id }).strict()),
  // D34: `acceptance_criteria` and `start` let the assistant hand a diagnosed fix to the builder/reviewer/Maestro
  // (`start` = placement `start_now`, maintainer). Bounded so four such cards stay near the 16 KB metadata budget.
  actionVariant(
    "create_task",
    z
      .object({
        project_id: id,
        title: z.string().trim().min(1).max(200),
        description: z.string().max(2000).default(""),
        acceptance_criteria: z.array(z.string().trim().min(1).max(200)).max(6).optional(),
        start: z.boolean().optional(),
        source_turn_id: id.optional(),
      })
      .strict(),
  ),
  actionVariant("add_guidance", z.object({ task_id: id, text: z.string().trim().min(1).max(2000) }).strict()),
  // D37.2: `prepare_command` — the assistant may only PROPOSE the dependency install command; the person sets it there.
  actionVariant("open_settings_form", z.object({ project_id: id, field: z.enum(["repository_url", "base_branch", "prepare_command"]) }).strict()),
  actionVariant("decide_approval", z.object({ approval_id: id, option: z.enum(["1", "2", "3", "4"]) }).strict()),
  actionVariant("connect_repository", z.object({ project_id: id }).strict()),
  actionVariant(
    "create_project_from_brief",
    z.object({ name: z.string().trim().min(1).max(ASSISTANT_BRIEF_NAME_MAX), brief: z.string().trim().min(1).max(ASSISTANT_BRIEF_MAX) }).strict(),
  ),
  // ---------- D34 ----------
  actionVariant("update_project_settings", projectSettingsArgs),
  actionVariant("set_execution_policy", z.object({ project_id: id, enabled: z.boolean() }).strict()),
  actionVariant("edit_task", editTaskArgs),
  actionVariant("move_task", z.object({ task_id: id, action: z.enum(ASSISTANT_MOVE_TASK_ACTIONS), reason: shortReason.optional() }).strict()),
  actionVariant("cancel_task", z.object({ task_id: id, reason: shortReason }).strict()),
  actionVariant("start_task_run", z.object({ task_id: id }).strict()),
  actionVariant("pause_project", z.object({ project_id: id, reason: shortReason.optional() }).strict()),
  actionVariant("drain_project", z.object({ project_id: id }).strict()),
  actionVariant("run_model_probe", z.object({ model_target_id: id, max_cost_cents: z.number().positive().max(ASSISTANT_PROBE_MAX_CENTS) }).strict()),
  actionVariant("update_agent_profile", agentProfileArgs),
  actionVariant("update_model_target", z.object({ model_target_id: id, state: z.enum(["unverified", "disabled"]) }).strict()),
  actionVariant("update_provider_connection", z.object({ provider_connection_id: id, state: z.enum(["draft", "disabled"]) }).strict()),
  actionVariant("activate_integration", z.object({ integration_id: id }).strict()),
  actionVariant("activate_routing_config", z.object({ routing_config_id: id }).strict()),
  actionVariant("publish_project", z.object({ project_id: id }).strict()),
  // ---------- D36 ----------
  actionVariant("start_planning_run", z.object({ project_id: id }).strict()),
  actionVariant("create_project_request", z.object({ project_id: id, session_id: id.optional(), source_turn_id: id.optional(), title: z.string().trim().min(1).max(200), brief: z.string().trim().min(1).max(4000), start: z.boolean().default(false) }).strict()),
  // `project_id` is always overwritten by the domain with the MaestrOS project (none known ⇒ the proposal is dropped).
  actionVariant(
    "create_system_task",
    z
      .object({
        project_id: id.optional(),
        source_turn_id: id.optional(),
        title: z.string().trim().min(1).max(200),
        description: z.string().max(2000).default(""),
        acceptance_criteria: z.array(z.string().trim().min(1).max(200)).max(6).optional(),
        start: z.boolean().optional(),
      })
      .strict(),
  ),
  actionVariant("run_guardian", z.object({}).strict()),
  actionVariant("guardian_incident_action", z.object({ incident_id: id, action: z.enum(["restart", "redeploy"]) }).strict()),
  actionVariant("review_learnings", z.object({}).strict()),
  actionVariant("server_operation", z.object({ server_id: id, project_id: id.optional(), command: z.string().trim().min(1).max(16000), disruption: z.enum(["none", "application", "host"]) }).strict()),
  actionVariant("github_operation", githubOperationRequestSchema),
  // ---------- D37.3 ----------
  actionVariant("translate_tasks", z.object({ project_id: id }).strict()),
  // ---------- RF-DEL-01 ----------
  actionVariant("delete_project", z.object({ project_id: id }).strict()),
]);
export type AssistantAction = z.infer<typeof assistantActionSchema>;

/**
 * D41-B2: how the server action executor runs an automatic action, or null when it stays with the browser or a click:
 * never a refused one, in a Home session only the catalog's `homeAuto` types, and never one that starts agent work
 * (`start`) or moves a task forward (only pause/block). "inline" is database-only (one transaction with its claim);
 * "effect" also reaches the engine. On a turn marked `auto_executor: "server"` the browser skips every non-null one.
 */
export function assistantServerActionClass(action: AssistantAction, home: boolean): "inline" | "effect" | null {
  if (action.mode !== "auto" || action.refused !== undefined) return null;
  if (home && ASSISTANT_ACTION_CATALOG[action.type].homeAuto !== true) return null;
  switch (action.type) {
    case "create_task":
    case "create_system_task":
    case "create_project_request":
      return action.args.start === true ? null : "inline";
    case "edit_task":
    case "add_guidance":
      return "inline";
    case "move_task":
      return action.args.action === "pause" || action.args.action === "block" ? "effect" : null;
    case "pause_project":
    case "drain_project":
    case "validate_project":
      return "effect";
    default:
      return null;
  }
}

// ---------- Reply (stored on `conversation_messages.metadata`, ≤16 KB) ----------

/** A parameterized rules-mode line (i18n key + values), never free text composed server-side. */
export const assistantRulesLineSchema = z
  .object({
    code: z.string().max(80),
    /** At most 2 substitution values (16 KB metadata budget, spec §3.1): a short, fixed-shape message, not free text. */
    params: z
      .record(z.string().max(16), z.union([z.string().max(24), z.number()]))
      .refine((params) => Object.keys(params).length <= 2, { message: "at most 2 params" }),
  })
  .strict();
export type AssistantRulesLine = z.infer<typeof assistantRulesLineSchema>;

const assistantRefSchema = z.object({ type: z.string().max(16), id: z.string().max(40), href: z.string().max(60) }).strict();

/** How a claimed action ran (R32): by itself right after the reply (`auto`), from a card click (`click`), or by the
 * server action executor (`server`, D41-B2; never accepted from a browser). Absent on claims stored before it existed. */
export const ASSISTANT_CLAIM_VIA = ["auto", "click", "server"] as const;
export type AssistantClaimVia = (typeof ASSISTANT_CLAIM_VIA)[number];
/** What a browser may claim with: the server owner is reserved for the executor. */
export const ASSISTANT_CLIENT_CLAIM_VIA = ["auto", "click"] as const satisfies readonly AssistantClaimVia[];

const assistantClaimSchema = z
  .object({ by: id, at: timestamp, outcome: z.enum(["pending", "succeeded", "failed", "unknown"]), detail: z.string().max(120).nullable(), via: z.enum(ASSISTANT_CLAIM_VIA).optional() })
  .strict();

/** Generated files use owner-only attachment storage and an exact reply reference. */
export const assistantGeneratedArtifactSchema = z.object({
  id, name: z.string().min(1).max(255), kind: z.enum(["text", "image"]),
  mime: z.string().min(1).max(100), size_bytes: z.number().int().min(1).max(262144), sha256: z.string().regex(/^[a-f0-9]{64}$/),
  complete: z.boolean().optional(), reviewed: z.boolean().optional(), continuation_required: z.boolean().optional(),
  review_kind: z.enum(["model", "structural"]).optional(), independent_review: z.literal("unknown").optional(),
});
export type AssistantGeneratedArtifact = z.infer<typeof assistantGeneratedArtifactSchema>;
export const assistantArtifactDraftSchema = z.object({
  name: z.string().trim().min(1).max(200).regex(/^[^/\\\x00-\x1f\x7f<>"']+$/),
  format: z.enum(["text", "markdown", "csv", "html", "svg", "slides"]),
  content: z.string().min(1).max(8000).refine(v => !v.includes("\0")),
}).strict();
export type AssistantArtifactDraft = z.infer<typeof assistantArtifactDraftSchema>;
export const assistantProjectRequestSchema = z.object({
  source_turn_id: id.optional(),
  session_id: id, title: z.string().trim().min(1).max(200), brief: z.string().trim().min(1).max(4000), start: z.boolean().default(false),
}).strict();

export const assistantReplySchema = z.object({
  mode: z.enum(ASSISTANT_REPLY_MODES),
  text: z.string().max(4000),
  lines: z.array(assistantRulesLineSchema).max(12).default([]),
  suggestions: z.array(z.string().max(80)).max(4).default([]),
  actions: z.array(assistantActionSchema).max(4).default([]),
  refs: z.array(assistantRefSchema).max(10).default([]),
  artifacts: z.array(assistantGeneratedArtifactSchema).max(3).optional(),
  cost_cents: z.number().nonnegative().nullable(),
  model: z.string().max(100).nullable(),
  agent_name: z.string().max(100).optional(),
  /** Turn diagnostics (REQ-7 AC04): agent profile, its immutable version and the effective capability (tool) names offered in the turn. Ids and names only. */
  diagnostics: z
    .object({ profile_id: id, profile_version_id: id.nullable(), capabilities: z.array(z.string().max(60)).max(80) })
    .strict()
    .optional(),
  model_call_ids: z.array(id).max(4).default([]),
  turn_id: id,
  /** The turn read untrusted content (learnings, PRD/task/log free text): auto actions are downgraded (§6/§9.7). */
  tainted: z.boolean(),
  budget: z.object({ day_spent_cents: z.number().nonnegative().nullable(), day_limit_cents: z.number().nonnegative() }).nullable(),
  /** Tool profile, version and effective capability list the turn ran with (diagnostics, AC04). */
  tool_profile: z
    .object({
      profile: z.string().max(100),
      version: z.string().max(100),
      session: z.enum(["home", "project"]),
      org_role: z.string().max(40),
      available: z.array(z.string().max(100)).max(100),
      unavailable: z.array(z.object({ name: z.string().max(100), status: z.string().max(40), required_org_role: z.string().max(40).nullable() })).max(100),
    })
    .optional(),
  /** ISO instant of the data the reply was computed from (never claims live data without it). */
  data_as_of: timestamp,
  /**
   * Outcome ledger of proposed/executed actions, keyed by action index (16 KB metadata budget,
   * spec §3.1). Every executed action is claimed first (auto right after the reply, confirm on
   * click, §9 C6), so this can reach one entry per action — at most 4, matching `actions` above.
   */
  claims: z
    .record(z.string().max(16), assistantClaimSchema)
    .refine((claims) => Object.keys(claims).length <= 4, { message: "at most 4 claims" })
    .default({}),
});
export type AssistantReply = z.infer<typeof assistantReplySchema>;

// ---------- Turn and message DTOs ----------

// ---------- D36 attachments (POST /v1/assistant/attachments) ----------

/** Caps of a composer attachment: per file, per message, and the text of one file that reaches the model. */
export const ASSISTANT_ATTACHMENT_MAX_BYTES = 10 * 1024 * 1024;
export const ASSISTANT_ATTACHMENTS_PER_MESSAGE = 5;
export const ASSISTANT_ATTACHMENT_TEXT_MAX_CHARS = 20_000;
export const ASSISTANT_ATTACHMENT_NAME_MAX = 255;
/**
 * Kinds of a composer attachment (D36; widened by D36.4, migration 0025): `text` (text, code, Markdown, JSON, CSV, logs),
 * `image` (raster images and SVG), `pdf`, and `document` (Office files: DOCX, whose text reaches the model, and XLSX, a
 * "data file" note).
 */
export const ASSISTANT_ATTACHMENT_KINDS = ["text", "image", "pdf", "document"] as const;
export type AssistantAttachmentKind = (typeof ASSISTANT_ATTACHMENT_KINDS)[number];
/** Every accepted image type (the API checks the bytes and stores the type the bytes prove). */
export const ASSISTANT_ATTACHMENT_IMAGE_TYPES = [
  "image/png", "image/jpeg", "image/webp", "image/gif", "image/bmp", "image/tiff", "image/avif", "image/heic", "image/heif", "image/svg+xml",
] as const;
export type AssistantAttachmentImageType = (typeof ASSISTANT_ATTACHMENT_IMAGE_TYPES)[number];
/**
 * The image types a model may receive as image content (when its target accepts images). The others reach the model as a
 * note (BMP, TIFF, AVIF, HEIC/HEIF: no conversion without a new dependency) or, for SVG, as its bounded source text.
 */
export const ASSISTANT_ATTACHMENT_MODEL_IMAGE_TYPES = ["image/png", "image/jpeg", "image/webp", "image/gif"] as const;
export const ASSISTANT_ATTACHMENT_SVG_TYPE = "image/svg+xml";
export const ASSISTANT_ATTACHMENT_DOCX_TYPE = "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
export const ASSISTANT_ATTACHMENT_XLSX_TYPE = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
export const ASSISTANT_ATTACHMENT_DOCUMENT_TYPES = [ASSISTANT_ATTACHMENT_DOCX_TYPE, ASSISTANT_ATTACHMENT_XLSX_TYPE] as const;
/** Text, code, Markdown, JSON, CSV and logs (never executed: they reach the model as bounded, redacted text). */
export const ASSISTANT_ATTACHMENT_TEXT_EXTENSIONS = [
  "txt", "md", "markdown", "json", "jsonl", "csv", "tsv", "log", "yaml", "yml", "toml", "ini", "xml", "html", "css", "scss",
  "js", "jsx", "mjs", "cjs", "ts", "tsx", "py", "rb", "go", "rs", "java", "kt", "swift", "c", "h", "cpp", "hpp", "cs", "php",
  "sh", "bash", "zsh", "sql", "graphql", "proto", "vue", "svelte", "diff", "patch", "rst", "tex", "conf", "cfg", "properties",
  "dart", "lua", "r",
] as const;
const IMAGE_EXTENSIONS: Readonly<Record<string, AssistantAttachmentImageType>> = {
  png: "image/png", jpg: "image/jpeg", jpeg: "image/jpeg", jfif: "image/jpeg", webp: "image/webp", gif: "image/gif", bmp: "image/bmp",
  tif: "image/tiff", tiff: "image/tiff", avif: "image/avif", heic: "image/heic", heif: "image/heif", svg: "image/svg+xml",
};
/** Declared image types browsers and operating systems use, to the canonical type. */
const IMAGE_TYPE_ALIASES: Readonly<Record<string, AssistantAttachmentImageType>> = {
  "image/png": "image/png", "image/apng": "image/png", "image/jpeg": "image/jpeg", "image/jpg": "image/jpeg", "image/pjpeg": "image/jpeg",
  "image/webp": "image/webp", "image/gif": "image/gif", "image/bmp": "image/bmp", "image/x-bmp": "image/bmp", "image/x-ms-bmp": "image/bmp",
  "image/tiff": "image/tiff", "image/tif": "image/tiff", "image/avif": "image/avif", "image/heic": "image/heic", "image/heic-sequence": "image/heic",
  "image/heif": "image/heif", "image/heif-sequence": "image/heif", "image/svg+xml": "image/svg+xml",
};
const DOCUMENT_EXTENSIONS: Readonly<Record<string, (typeof ASSISTANT_ATTACHMENT_DOCUMENT_TYPES)[number]>> = { docx: ASSISTANT_ATTACHMENT_DOCX_TYPE, xlsx: ASSISTANT_ATTACHMENT_XLSX_TYPE };
const TEXT_MIME = /^(text\/[a-z0-9.+-]+|application\/(json|x-ndjson|xml|x-yaml|yaml|toml|sql|javascript|typescript|x-sh))$/;
/** Declared types that say nothing about the content (the extension decides). */
const GENERIC_TYPES = new Set(["", "application/octet-stream", "binary/octet-stream", "application/zip", "application/x-zip-compressed"]);

/**
 * The kind and canonical type of a file by its name and declared type, or null when it is not accepted (D36). Shared by
 * the API (which also checks the bytes: UTF-8 for text, magic numbers for images, PDFs and Office files, and stores the
 * image type the bytes prove) and the web (early refusal).
 */
export function classifyAssistantAttachment(name: string, declaredType: string): { kind: AssistantAttachmentKind; mime: string } | null {
  const ext = /\.([A-Za-z0-9]{1,10})$/.exec(name)?.[1]?.toLowerCase() ?? "";
  const type = declaredType.split(";")[0]?.trim().toLowerCase() ?? "";
  if (type === "application/pdf" || ext === "pdf") return ext === "pdf" || ext === "" ? { kind: "pdf", mime: "application/pdf" } : null;
  // A screenshot named .png that is really a JPEG still declares an image type: the API stores what the bytes prove.
  const image = IMAGE_EXTENSIONS[ext];
  if (image) return GENERIC_TYPES.has(type) || type.startsWith("image/") ? { kind: "image", mime: image } : null;
  const document = DOCUMENT_EXTENSIONS[ext];
  if (document) return GENERIC_TYPES.has(type) || type === document ? { kind: "document", mime: document } : null;
  const isText = (ASSISTANT_ATTACHMENT_TEXT_EXTENSIONS as readonly string[]).includes(ext);
  // A pasted image ("image", or a name without a known extension) is classified by its declared type.
  const aliased = IMAGE_TYPE_ALIASES[type];
  if (aliased) return isText ? null : { kind: "image", mime: aliased };
  if (ext === "" && (ASSISTANT_ATTACHMENT_DOCUMENT_TYPES as readonly string[]).includes(type)) return { kind: "document", mime: type };
  if (isText) return { kind: "text", mime: TEXT_MIME.test(type) ? type : "text/plain" };
  if (ext === "" && TEXT_MIME.test(type)) return { kind: "text", mime: type };
  return null;
}

/** The `accept` attribute of a composer file input: every type `classifyAssistantAttachment` accepts. */
export const ASSISTANT_ATTACHMENT_ACCEPT = [
  "image/*",
  ...Object.keys(IMAGE_EXTENSIONS).map((e) => `.${e}`),
  ".pdf",
  ...Object.keys(DOCUMENT_EXTENSIONS).map((e) => `.${e}`),
  ...ASSISTANT_ATTACHMENT_TEXT_EXTENSIONS.map((e) => `.${e}`),
].join(",");

/** Ids of the attachments sent with a request (a turn, a brief, a planning run): at most 5, distinct. */
export const assistantAttachmentIdsSchema = z
  .array(id)
  .max(ASSISTANT_ATTACHMENTS_PER_MESSAGE)
  .refine((ids) => new Set(ids).size === ids.length, { message: "attachments must be distinct" });

/** What a turn and the conversation history show of an attachment (chips): never its content. */
export const assistantAttachmentRefSchema = z
  .object({
    id,
    name: z.string().min(1).max(ASSISTANT_ATTACHMENT_NAME_MAX),
    size_bytes: z.number().int().min(1).max(ASSISTANT_ATTACHMENT_MAX_BYTES),
    mime: z.string().min(1).max(100),
    kind: z.enum(ASSISTANT_ATTACHMENT_KINDS),
  })
  .strict();
export type AssistantAttachmentRef = z.infer<typeof assistantAttachmentRefSchema>;

/** `POST /v1/assistant/attachments` response. */
export const assistantAttachmentSchema = assistantAttachmentRefSchema.extend({ project_id: id.nullable(), created_at: timestamp });
export type AssistantAttachment = z.infer<typeof assistantAttachmentSchema>;

/** Stored on the user message under `metadata.turn` (no separate turn table, spec §3.6). */
export const assistantTurnMetaSchema = z
  .object({
    id,
    state: z.enum(ASSISTANT_TURN_STATES),
    user_id: id,
    context: assistantContextSchema,
    created_at: timestamp,
    /** Observed worker lifecycle times; absent on historical turns. */
    started_at: timestamp.optional(),
    completed_at: timestamp.optional(),
    /** Public answer deltas and fixed tool phases, never private reasoning. */
    progress: z.object({
      phase: z.enum(["model", "tools", "answer"]),
      text: z.string().max(12000),
      revision: z.number().int().nonnegative(),
      updated_at: timestamp,
    }).strict().optional(),
    /** D36: the files sent with the question (display facts only; the engine reads the content by id). */
    attachments: z.array(assistantAttachmentRefSchema).max(ASSISTANT_ATTACHMENTS_PER_MESSAGE).optional(),
    /** D41-B2: this turn's automatic actions and follow-ups are run by the server executor; the browser only watches. */
    auto_executor: z.enum(["server"]).optional(),
  })
  .strict();
export type AssistantTurnMeta = z.infer<typeof assistantTurnMetaSchema>;

export const assistantTurnRequestSchema = z
  .object({
    session_id: id.optional(),
    project_id: id.nullable(),
    text: z.string().trim().min(1).max(4000),
    context: assistantContextSchema,
    /** D36: ids of attachments uploaded by the same user in the same scope (Home or this project), at most 5. */
    attachments: assistantAttachmentIdsSchema.optional(),
  })
  .strict();
export type AssistantTurnRequest = z.infer<typeof assistantTurnRequestSchema>;

export const assistantMessageSchema = z.object({
  id,
  session_id: id,
  sequence: z.number().int().nonnegative(),
  author_role: z.enum(MESSAGE_ROLES),
  content: z.string().max(4000),
  created_at: timestamp,
  turn: assistantTurnMetaSchema.nullable(),
  reply: assistantReplySchema.nullable(),
});
export type AssistantMessage = z.infer<typeof assistantMessageSchema>;

/** `rules_inline`: the reply is synchronous and returned inline; `started`: poll the session for it. */
export const assistantTurnAcceptedSchema = z
  .object({ session_id: id, turn_id: id, execution: z.enum(["started", "rules_inline"]), reply: assistantMessageSchema.nullable() })
  .strict();
export type AssistantTurnAccepted = z.infer<typeof assistantTurnAcceptedSchema>;

// ---------- Usage monitoring (spec §3.9, owner request; GET /v1/assistant/usage) ----------

export const assistantUsageSchema = z.object({
  days: z.array(
    z.object({
      day: z.string(),
      /** Null while any of the day's turns has an unknown cost never charged to a settled reservation (M9, R13). */
      spent_cents: z.number().nullable(),
      /** The partial, explicit known total even when `spent_cents` is null. */
      known_cents: z.number(),
      questions: z.number().int(),
      limit_cents: z.number(),
    }),
  ),
  today: z.object({ spent_cents: z.number().nullable(), limit_cents: z.number() }),
  per_question: z.object({
    avg_cents: z.number().nullable(),
    max_cents: z.number().nullable(),
    stopped_at_ceiling: z.number().int(),
    /** True while any visible turn in the window has an unknown cost (fix round 1, minor #4): avg/max ignore it. */
    partial: z.boolean(),
  }),
  /** Keyed by `AssistantReplyMode`; every mode is present, zero when never seen in the window. */
  modes: z.record(z.string(), z.number().int()),
  /** `project_id` null is the "Home" bucket (fix round 1, R24) — web shows the label, not this field, for it. */
  by_project: z.array(
    z.object({
      project_id: id.nullable(),
      name: z.string().nullable(),
      /** Null while the project's spend has an unknown-cost call (same rule as `days[]`). */
      spent_cents: z.number().nullable(),
      /** The partial, explicit known total even when `spent_cents` is null. */
      known_cents: z.number(),
      questions: z.number().int(),
    }),
  ),
  by_user: z.array(z.object({ user_id: id, display_name: z.string().nullable(), spent_cents: z.number().nullable(), questions: z.number().int() })),
  actions: z.object({ proposed: z.number().int(), auto_executed: z.number().int(), clicked: z.number().int(), failed: z.number().int() }),
  coverage: z.object({ unknown_cost_calls: z.number().int() }),
});
/** Same shape as `assistantUsageSchema`, with `modes` narrowed to a complete record (every `AssistantReplyMode` key present). */
export type AssistantUsage = Omit<z.infer<typeof assistantUsageSchema>, "modes"> & { modes: Record<AssistantReplyMode, number> };

// ---------- Configuration (routing config `assistant` section, council condition 4) ----------

const ceilings = { per_question_cents: z.number().min(0).max(100), per_day_cents: z.number().min(0).max(100_000) };

export const assistantConfigSchema = z
  .object({ per_question_cents: ceilings.per_question_cents.default(5), per_day_cents: ceilings.per_day_cents.default(100) })
  .strict();

/**
 * The `assistant` section of one routing LAYER (global or project): no defaults — they apply only at resolution
 * (domain STATIC_FIELDS). A default here would pin every project layer to 5/100: an always-rejected per-day override
 * (organization-only) and a per-question `min` that stops the organization from ever raising the ceiling.
 */
export const assistantConfigLayerSchema = z.object(ceilings).partial().strict();
export type AssistantConfig = z.infer<typeof assistantConfigSchema>;
export const DEFAULT_ASSISTANT_CONFIG: AssistantConfig = assistantConfigSchema.parse({});
